/**
 * Care-app authentication helpers: Microsoft (Entra ID) sign-in and linking,
 * multi-factor sign-in/enrollment, and the security callables in
 * functions/https/careAuth.js.
 *
 * Design: docs/PLAN_SECURITY_HARDENING_2026_10.md
 */
import {
  OAuthProvider,
  signInWithPopup,
  linkWithPopup,
  getMultiFactorResolver,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  getIdTokenResult,
} from 'firebase/auth';
import { auth, functions, httpsCallable } from '../firebase';

/** Trilogy Health Entra directory (tenant) — restricts the Microsoft picker to work accounts. */
export const MICROSOFT_TENANT_ID =
  process.env.REACT_APP_MS_TENANT_ID || '496c3d99-ed00-4ffd-9439-9a5db295c674';

export const MICROSOFT_PROVIDER_ID = 'microsoft.com';

/** Domains whose users must sign in with Microsoft (mirrors securityConfig.ssoRequiredDomains). */
export const SSO_REQUIRED_DOMAINS = ['trilogy-health.com'];

export const SIGN_OUT_REASON_KEY = 't1care:signOutReason';

export function emailDomain(email) {
  const s = String(email || '').toLowerCase().trim();
  const i = s.lastIndexOf('@');
  return i === -1 ? '' : s.slice(i + 1);
}

export function isSsoRequiredEmail(email) {
  return SSO_REQUIRED_DOMAINS.includes(emailDomain(email));
}

export function buildMicrosoftProvider() {
  const provider = new OAuthProvider(MICROSOFT_PROVIDER_ID);
  provider.setCustomParameters({ tenant: MICROSOFT_TENANT_ID, prompt: 'select_account' });
  provider.addScope('email');
  provider.addScope('openid');
  provider.addScope('profile');
  return provider;
}

export async function signInWithMicrosoft() {
  return signInWithPopup(auth, buildMicrosoftProvider());
}

/** Links Microsoft to the signed-in (temp-password) staff account, then removes the password server-side. */
export async function linkMicrosoftAndFinalize() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in.');
  // Idempotent: if a previous attempt linked Microsoft but finalize never ran
  // (network drop, old client), skip the popup and just finish server-side.
  if (!hasMicrosoftProvider(user)) {
    try {
      await linkWithPopup(user, buildMicrosoftProvider());
    } catch (err) {
      if (err?.code !== 'auth/provider-already-linked') throw err;
    }
  }
  const fn = httpsCallable(functions, 'finalizeMicrosoftLink');
  const res = await fn({});
  await user.getIdToken(true);
  return res.data;
}

export function hasMicrosoftProvider(user) {
  return Boolean((user?.providerData || []).find((p) => p.providerId === MICROSOFT_PROVIDER_ID));
}

export function hasPasswordProvider(user) {
  return Boolean((user?.providerData || []).find((p) => p.providerId === 'password'));
}

// ---------------------------------------------------------------------------
// Session claims
// ---------------------------------------------------------------------------

/**
 * @returns {{ authTime: number|null, secondFactor: string|null, idpMfa: boolean, signInCountry: string|null, signInProvider: string|null }}
 */
export async function readSessionClaims(user, forceRefresh = false) {
  const u = user || auth.currentUser;
  if (!u) return { authTime: null, secondFactor: null, idpMfa: false, signInCountry: null, signInProvider: null };
  const r = await getIdTokenResult(u, forceRefresh);
  const c = r?.claims || {};
  const fb = c.firebase || {};
  return {
    authTime: c.auth_time ? Number(c.auth_time) : null,
    secondFactor: fb.sign_in_second_factor || null,
    idpMfa: c.idpMfa === true,
    signInCountry: c.signInCountry || null,
    signInProvider: fb.sign_in_provider || null,
  };
}

export function secondFactorSatisfied(claims) {
  return Boolean(claims?.secondFactor) || claims?.idpMfa === true;
}

// ---------------------------------------------------------------------------
// MFA: sign-in resolver
// ---------------------------------------------------------------------------

export function isMfaRequiredError(err) {
  return err?.code === 'auth/multi-factor-auth-required';
}

export function resolverFromError(err) {
  return getMultiFactorResolver(auth, err);
}

/** Invisible reCAPTCHA needed by Firebase for SMS flows. Mount target must exist in the DOM. */
export function createInvisibleRecaptcha(containerId) {
  return new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
}

/** Sends the SMS for a phone hint during sign-in; returns the verificationId. */
export async function sendSmsForResolver(resolver, hint, recaptchaVerifier) {
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber({ multiFactorHint: hint, session: resolver.session }, recaptchaVerifier);
}

export async function resolveWithSms(resolver, verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code.trim());
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  return resolver.resolveSignIn(assertion);
}

export async function resolveWithTotp(resolver, hint, code) {
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code.trim());
  return resolver.resolveSignIn(assertion);
}

// ---------------------------------------------------------------------------
// MFA: enrollment (signed-in user)
// ---------------------------------------------------------------------------

export function enrolledFactors(user) {
  const u = user || auth.currentUser;
  if (!u) return [];
  try {
    return multiFactor(u).enrolledFactors || [];
  } catch {
    return [];
  }
}

export async function startSmsEnrollment(phoneE164, recaptchaVerifier) {
  const user = auth.currentUser;
  const session = await multiFactor(user).getSession();
  const provider = new PhoneAuthProvider(auth);
  return provider.verifyPhoneNumber({ phoneNumber: phoneE164, session }, recaptchaVerifier);
}

export async function finishSmsEnrollment(verificationId, code, displayName = 'Text message') {
  const user = auth.currentUser;
  const cred = PhoneAuthProvider.credential(verificationId, code.trim());
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, displayName);
  await user.getIdToken(true);
}

/** @returns {{ secret: import('firebase/auth').TotpSecret, otpauthUrl: string }} */
export async function startTotpEnrollment(accountEmail) {
  const user = auth.currentUser;
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const otpauthUrl = secret.generateQrCodeUrl(accountEmail || user.email || 'user', 'Trilogy One Care');
  return { secret, otpauthUrl };
}

export async function finishTotpEnrollment(secret, code, displayName = 'Authenticator app') {
  const user = auth.currentUser;
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.trim());
  await multiFactor(user).enroll(assertion, displayName);
  await user.getIdToken(true);
}

export async function unenrollFactor(factor) {
  const user = auth.currentUser;
  await multiFactor(user).unenroll(factor);
  await user.getIdToken(true);
}

/** Normalizes a US phone entry to E.164. Returns null when it cannot. */
export function toE164US(input) {
  const digits = String(input || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (String(input || '').trim().startsWith('+') && digits.length >= 8) return `+${digits}`;
  return null;
}

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function completeFirstLoginPasswordChange() {
  const fn = httpsCallable(functions, 'completeFirstLoginPasswordChange');
  const res = await fn({});
  return res.data;
}

export async function resetCareUserMfa(uid) {
  const fn = httpsCallable(functions, 'resetCareUserMfa');
  return (await fn({ uid })).data;
}

export async function extendMfaGrace(uid, days) {
  const fn = httpsCallable(functions, 'extendMfaGrace');
  return (await fn({ uid, days })).data;
}

export async function certifyExternalUser(uid) {
  const fn = httpsCallable(functions, 'certifyExternalUser');
  return (await fn({ uid })).data;
}

export async function recordIdleTimeout(idleMinutes) {
  try {
    const fn = httpsCallable(functions, 'recordClientSecurityEvent');
    await fn({ type: 'session_idle_timeout', idleMinutes });
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// Error copy
// ---------------------------------------------------------------------------

/**
 * Blocking-function rejections reach the client as `auth/internal-error` with
 * the HttpsError JSON embedded in the message. Pull the human message out.
 */
export function blockingFunctionMessage(err) {
  const raw = String(err?.message || '');
  const m = raw.match(/\{.*\}/s);
  if (!m) return null;
  try {
    const parsed = JSON.parse(m[0]);
    return parsed?.error?.message || null;
  } catch {
    return null;
  }
}

export function friendlyAuthError(err, fallback = 'Sign in failed.') {
  const code = err?.code || '';
  const blocked = blockingFunctionMessage(err);
  if (blocked) return blocked;
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The Microsoft sign-in window was closed before finishing.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the Microsoft sign-in window. Allow pop-ups for this site and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists for this email with a different sign-in method. Sign in with your password first, then connect Microsoft.';
    case 'auth/credential-already-in-use':
      return 'That Microsoft account is already connected to a different Trilogy One user.';
    case 'auth/invalid-verification-code':
      return 'That code is not correct. Check it and try again.';
    case 'auth/code-expired':
      return 'That code has expired. Request a new one.';
    case 'auth/requires-recent-login':
      return 'For security, sign out and sign in again before changing this setting.';
    case 'auth/unverified-email':
      return 'Your email address has not been verified on this account, so two-step verification cannot be set up yet. Contact your administrator.';
    case 'auth/unauthorized-domain':
      return 'This web address is not authorized for sign-in. Use https://care.trilogy-health.com.';
    case 'auth/provider-already-linked':
      return 'A Microsoft account is already connected to this user.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Contact your administrator.';
    case 'auth/second-factor-already-in-use':
      return 'That phone number or authenticator is already enrolled.';
    case 'auth/maximum-second-factor-count-exceeded':
      return 'You have reached the maximum number of verification methods.';
    case 'auth/invalid-phone-number':
      return 'Enter a valid mobile phone number.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return err?.message || fallback;
  }
}
